import React, { useCallback, useMemo, useRef, useState } from "react";
import type { TrackHistoryUpload } from "../types/disputes";
import "./TrackHistoryUploader.css";
import { copyTextToClipboard } from "../lib/clipboard";
import { formatBytes } from "../utils/formatBytes";
import { classifyUploadExtension } from "../utils/uploadFileKind";
import { publicUrl } from "../utils/publicUrl";
import { t } from "../i18n";
import { T } from "../i18n/T";

// Reusable file-upload UI for proactive track-history attachment.
//
// Used in two flows:
//   1. CashoutProtectModal — proactive, posts to /api/orders/upload-history
//      with orderIds[] for every played order in the just-completed cashout.
//   2. PayoutHistoryPage chargeback ⚠ — reactive, posts to
//      /api/disputes/upload-for-payout/:batchId so the server attaches the
//      file to each open dispute for that payout.
//
// The component only owns the picker / validation / submit UX. Callers pass
// uploadEndpoint + extraFields so the same component serves both flows
// without if-chains on the inside.

const ACCEPTED_EXTENSIONS = [".csv", ".txt", ".xml", ".m3u", ".m3u8", ".png", ".jpg", ".jpeg"];
const ACCEPT_ATTR = ACCEPTED_EXTENSIONS.join(",");
const MAX_BYTES = 25 * 1024 * 1024;

interface Props {
  // The upload itself (ordersApi.uploadHistory, disputesApi.uploadForPayout).
  // Both answer the same shape, so the component doesn't care which one runs.
  upload: (file: FormData) => Promise<TrackHistoryUpload>;
  // Optional non-file form fields appended alongside the file. Cashout
  // passes { orderIds: JSON.stringify([...]) }; the chargeback endpoint
  // takes nothing here.
  extraFields?: Record<string, string>;
  // Free-text shown above the dropzone. Use it to remind the talent which
  // set this file is for — "Saturday at Olé Olé, 14 orders, $420 total".
  contextLine?: string;
  onUploaded: (result: { stripeFileId: string; filename: string; fileType: "export" | "screenshot" }) => void;
  /** Omitted on a page with no modal to close (the "Wasn't played?" answer
      page): no Cancel button, the submit stands alone. */
  onCancel?: () => void;
  submitLabel?: string;
  /** What "Copy this link" copies. The Balance flow passes its canonical
      /balance?upload=1 deep link so the laptop lands with the uploader
      already open even when the modal was reached by tapping the amber
      card; default stays the current page (DisputeUploadPage IS its own
      deep link). */
  copyUrl?: string;
}

const TrackHistoryUploader: React.FC<Props> = ({
  upload,
  extraFields,
  contextLine,
  onUploaded,
  onCancel,
  submitLabel = t("trackHistoryUploader.attachToMyOrders"),
  copyUrl,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Mobile → computer hand-off: copies the current app URL so the talent
  // can finish the upload from a desktop browser if they prefer. Best-
  // effort with a legacy execCommand fallback for sandboxed/insecure
  // contexts that reject the Clipboard API (mirrors DisputeUploadPage).
  const onCopyLink = useCallback(() => {
    if (typeof window === "undefined") return;
    const url = copyUrl || publicUrl();
    copyTextToClipboard(url).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  }, [copyUrl]);

  const acceptedFileType = useMemo(() => (file ? classifyUploadExtension(file.name) : null), [file]);

  const handleFiles = useCallback((list: FileList | File[] | null) => {
    setFileError(null);
    setSubmitError(null);
    if (!list || list.length === 0) return;
    const first = list[0];
    const klass = classifyUploadExtension(first.name);
    if (!klass) {
      setFileError(t("trackHistoryUploader.thatFileTypeIsn", { ACCEPTED_EXTENSIONS: ACCEPTED_EXTENSIONS.join(", ") }));
      return;
    }
    if (first.size > MAX_BYTES) {
      setFileError(t("trackHistoryUploader.fileIsTooLarge", { formatBytes: formatBytes(first.size) }));
      return;
    }
    setFile(first);
  }, []);

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!file || submitting) return;

      setSubmitting(true);
      setSubmitError(null);

      const fd = new FormData();
      fd.append("file", file);
      if (extraFields) {
        for (const [k, v] of Object.entries(extraFields)) fd.append(k, v);
      }

      try {
        const data = await upload(fd);
        onUploaded({
          stripeFileId: data.stripeFileId,
          filename: data.filename,
          fileType: data.fileType,
        });
      } catch (err) {
        const e = err as { response?: { status?: number; data?: { msg?: string } } };
        const status = e?.response?.status;
        const msg = e?.response?.data?.msg;
        if (status === 413) setSubmitError(msg || t("trackHistoryUploader.fileTooLarge"));
        else if (status === 415) setSubmitError(msg || t("trackHistoryUploader.fileTypeNotAccepted"));
        else if (status === 409) setSubmitError(msg || t("trackHistoryUploader.theseOrdersCanT"));
        else if (status === 403) setSubmitError(msg || t("trackHistoryUploader.youDonTOwn"));
        else if (status === 404) setSubmitError(msg || t("trackHistoryUploader.couldnTFindThe"));
        else if (status === 502) setSubmitError(msg || t("trackHistoryUploader.weCouldnTStore"));
        else setSubmitError(t("trackHistoryUploader.uploadFailedPleaseTry"));
      } finally {
        setSubmitting(false);
      }
    },
    [file, submitting, upload, extraFields, onUploaded]
  );

  return (
    <form className="th-uploader" onSubmit={onSubmit}>
      {contextLine && <div className="th-uploader-context">{contextLine}</div>}

      <div
        className={`th-uploader-drop ${dragOver ? "is-drag" : ""} ${!file ? "is-empty" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer?.files || null);
        }}
        onClick={() => fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT_ATTR}
          style={{ display: "none" }}
          onChange={(e) => handleFiles(e.target.files)}
        />
        {file ? (
          <div className="th-uploader-selected">
            <div className="th-uploader-selected-name">{file.name}</div>
            <div className="th-uploader-selected-meta">
              {formatBytes(file.size)}
              {acceptedFileType ? (
                <>
                  {" · "}
                  <span className={`th-uploader-type th-uploader-type-${acceptedFileType}`}>
                    {acceptedFileType === "export"
                      ? t("trackHistoryUploader.strongEvidenceTrackHistory")
                      : t("trackHistoryUploader.acceptableScreenshot")}
                  </span>
                </>
              ) : null}
            </div>
          </div>
        ) : (
          <>
            {/* The composer's green icon, here too (founder: "make them
                coherent" — the bouncing grey arrow retired with it). */}
            <svg className="th-uploader-drop-icon" viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="#24C961" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M12 16V4M7 9l5-5 5 5" />
              <path d="M4 16v3a1 1 0 001 1h14a1 1 0 001-1v-3" />
            </svg>
            <div className="th-uploader-drop-title">{t("trackHistoryUploader.uploadTrackHistory")}</div>
            <div className="th-uploader-drop-sub">{t("trackHistoryUploader.orClickToBrowse")}</div>
          </>
        )}
      </div>

      {fileError && <div className="th-uploader-error">{fileError}</div>}

      <div className="th-uploader-copy-link" aria-live="polite">
        <span className="th-uploader-copy-link-text">{t("trackHistoryUploader.onMobileCopyThis")}</span>
        <button
          type="button"
          className="th-uploader-copy-link-btn"
          onClick={onCopyLink}
          aria-label={t("trackHistoryUploader.copyLinkToClipboard")}
          title={t("trackHistoryUploader.copyLink")}
        >
          {linkCopied ? (
            <span className="th-uploader-copy-link-btn-done">{t("trackHistoryUploader.copied")}</span>
          ) : (
            <>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              <span>{t("trackHistoryUploader.copy")}</span>
            </>
          )}
        </button>
      </div>

      <p className="th-uploader-helper">
        <T k="trackHistoryUploader.exportFromSeratoHistory" tags={{ 0: (c) => <strong>{c}</strong>, 1: (c) => <strong>{c}</strong>, 2: (c) => <strong>{c}</strong>, 3: (c) => <strong>{c}</strong>, 4: (c) => <strong>{c}</strong>, 5: (c) => <strong>{c}</strong> }} />
      </p>

      {submitError && <div className="th-uploader-error">{submitError}</div>}

      <div className="th-uploader-actions">
        {onCancel && (
          <button type="button" className="th-uploader-cancel" onClick={onCancel} disabled={submitting}>
            {t("trackHistoryUploader.cancel")}
          </button>
        )}
        <button type="submit" className="th-uploader-submit" disabled={!file || submitting}>
          {submitting ? t("trackHistoryUploader.uploading") : submitLabel}
        </button>
      </div>
    </form>
  );
};

export default TrackHistoryUploader;
